import React from 'react';
import { Currency } from '@common/components';
import { toUSFormat } from 'works/legacy/lib/date-helper';
import {
  useOpenStaffingRequestsDeviation,
  usePendingOffersDeviation,
  useRateCardCreationAttributes,
} from './_rates-long-term-context';
import {
  DeprecatedStatusPill,
  TextCapitalized,
} from '@institutions/components';
import { RatesLongTermUpdatesDeviationTable } from './_rates-long-term-step-updates-deviation-table';
import { BillingCardPendingOfferType } from '@institutions/graphql';
import { getRateByEmploymentType } from '@institutions/services';

export function RatesLongTermStepUpdatesTabPendingOffer({
  dataSource,
}: {
  dataSource?: BillingCardPendingOfferType[];
}) {
  const [rateCardAttributes] = useRateCardCreationAttributes();

  const [pendingOffers, { setAll, deviate, undeviate }] =
    usePendingOffersDeviation();
  const [openStaffingRequests] = useOpenStaffingRequestsDeviation();

  const tableColumns = [
    {
      dataIndex: ['staffingRequest', 'code'],
      key: 'id',
      title: 'Id',
    },
    {
      dataIndex: ['staffingRequestApplication', 'worksProfile', 'fullName'],
      key: 'candidate',
      title: 'Candidate',
    },
    {
      dataIndex: ['staffingRequestApplication', 'supplier', 'name'],
      key: 'supplier',
      title: 'Supplier',
    },
    {
      dataIndex: ['staffingRequest', 'organizationBusiness', 'name'],
      key: 'facility',
      title: 'Facility',
    },
    {
      dataIndex: 'shiftType',
      key: 'shiftType',
      title: 'Shift Type',
      render: (shiftType: string) => <TextCapitalized text={shiftType} />,
    },
    {
      dataIndex: 'startDate',
      key: 'startDate',
      title: 'Start Date',
      render: toUSFormat,
    },
    {
      dataIndex: 'endDate',
      key: 'endDate',
      title: 'End Date',
      render: toUSFormat,
    },
    {
      dataIndex: 'billingRegularRate',
      key: 'currentRate',
      title: 'Current Rate',
      render: (value: number) => <Currency value={value} />,
    },
    {
      dataIndex: 'offer',
      key: 'newRate',
      title: 'New Rate',
      render: (_: unknown, record: BillingCardPendingOfferType) => {
        const billingCardRate = getRateByEmploymentType(
          rateCardAttributes,
          record.shiftType,
          record.staffingRequestApplication.employmentType
        );

        return <Currency value={billingCardRate || 0} />;
      },
    },
    {
      dataIndex: 'state',
      key: 'status',
      title: 'Status',
      render: (status: string) => <DeprecatedStatusPill status={status} />,
    },
  ];

  const blockedDeviations = dataSource
    ?.filter(({ staffingRequest }) =>
      openStaffingRequests.includes(staffingRequest.id)
    )
    .map((offer) => offer.id);

  return (
    <RatesLongTermUpdatesDeviationTable
      dataSource={dataSource}
      columns={tableColumns}
      onDeviateList={setAll}
      onDeviateItem={deviate}
      onUndeviateItem={undeviate}
      deviatedIds={pendingOffers}
      blockedDeviationIds={blockedDeviations}
    />
  );
}
